import { ExecutionTriggeredType } from '@enums/triggered-type.enum';
import { PageObject } from '@models/page-object.model';
import { User } from '@models/user.model';
import { ReRunType } from '@modules/test-development/enums/re-run-type.enum';
import { ResultBase } from '@modules/test-development/models/result-base.model';
import { ResultConstant } from '@testsigma/organism';
import {
  SKIP,
  alias,
  custom,
  deserialize,
  getDefaultModelSchema,
  object,
  optional,
  serializable,
} from 'serializr';
import { CustomReRunRequest } from './custom-re-run-request.model';
import { EnvironmentResult } from './environment-result.model';
import { Environment } from './environment.model';
import { ExecutionDetails } from './execution-details.model';
import { TestCaseResult } from '@testsigma/organism';
import { TestDevice } from './test-device.model';
import { ExecutionConfig } from './test-plan-execution-config';
import { TestPlan } from './test-plan.model';
import { TestSuiteResult } from './test-suite-result.model';
import { AccessibilityTestingType } from '@enums/accessibility-testing-type.enum';

export class TestPlanResultMetric {

  @serializable( custom( (v) => SKIP, (v) => v, ), )
  public latestResult: ResultConstant;

  @serializable
  totalCount: number;

  @serializable
  failedCount: number;

  @serializable
  passedCount: number;

  @serializable
  stoppedCount: number;

  @serializable
  notExecutedCount: number;

  @serializable
  queuedCount: number;

  @serializable
  runningCount: number;

  @serializable
  public duration: number;

  @serializable
  public consolidatedDuration: number;

  @serializable
  consolidatedPlanTotalCount: number;

  @serializable
  consolidatedPlanFailedCount: number;

  @serializable
  consolidatedPlanPassedCount: number;

  @serializable
  consolidatedPlanStoppedCount: number;

  @serializable
  consolidatedPlanNotExecutedCount: number;

  @serializable
  consolidatedPlanQueuedCount: number;

  @serializable
  consolidatedPlanRunningCount: number;

  @serializable
  public accessibilityTestingType: AccessibilityTestingType;

  deserialize(input: any): this {
    return Object.assign(this, deserialize(TestPlanResultMetric, input));
  }

  isStepLevelApproach(){
    return this.accessibilityTestingType == AccessibilityTestingType.STEP_LEVEL_APPROACH;
  }

}

export class ExecutionResult extends ResultBase implements PageObject {
  constructor() {
    super();
    getDefaultModelSchema(ExecutionResult).props['lastReRunResult'] = optional(
      object(ExecutionResult),
    );
  }
  @serializable(
    custom(
      () => SKIP,
      (v) => {
        return new TestPlan().deserialize(v);
      },
    ),
  )
  public execution: TestPlan;

  @serializable(optional(object(ExecutionConfig)))
  public executionConfig: ExecutionConfig;

  @serializable(optional(object(ExecutionConfig)))
  public executionResultConfig: ExecutionConfig;

  @serializable
  public executionId: number;
  @serializable
  public message: string;
  @serializable(alias('executedBy'))
  public executedById: number;
  @serializable(
    optional(
      custom(
        () => SKIP,
        (v) => v,
      ),
    ),
  )
  public environmentResultsDTO: TestDevice[];
  @serializable
  public buildNo: string;
  @serializable
  public environmentId: number;
  @serializable(optional(object(Environment)))
  public environment: Environment;
  @serializable
  public reRunParentId: number;
  @serializable
  public reRunType: ReRunType;
  @serializable
  public isReRun: boolean;
  @serializable
  public canStopAll: boolean;
  @serializable
  public triggeredType: ExecutionTriggeredType;
  @serializable
  public parentExecutionResultId: number;
  @serializable
  public executionResultId: number;
  public executedBy: User;
  @serializable
  public totalRunningCount: number;
  @serializable
  public totalQueuedCount: number;
  @serializable
  public consolidatedVisualResult: ResultConstant;
  @serializable(
    custom(
      () => SKIP,
      (v) => v,
    ),
  )
  public lastRunId: number;
  @serializable
  public uploadVersionId: number;

  @serializable
  public consolidatedDuration: number;

  @serializable
  public runTestCasesInParallel: boolean;

  @serializable
  public runTestSuitesInParallel: boolean;
  @serializable(
    custom(
      (v) => SKIP,
      (v) => v,
    ),
  )
  public latestResult: ResultConstant;

  @serializable(optional(object(TestPlanResultMetric)))
  public testPlanResultMetric: TestPlanResultMetric;

  @serializable(optional(object(ExecutionDetails)))
  public executionDetails: ExecutionDetails;

  public lastChildResult: ExecutionResult;
  /**
   * to show Compare Run button in Run Result > Test Case View > Header
   * to show Previous Run data in Run Comparison Drawer > Header
   * to show Previous Run data in Run Comparison Drawer > Test Case/Suite/Machine row
   */
  @serializable
  public lastReRunResult: ExecutionResult;

  public testCaseResult: TestCaseResult;

  @serializable(
    custom(
      (v) => {
        return v;
      },
      (v) => {
        if (v.length == 0) return [];
        else return v;
      },
    ),
  )
  public testCaseResultRequests: CustomReRunRequest[];

  deserialize(input: any): this {
    return Object.assign(this, deserialize(ExecutionResult, input));
  }

  isDry() {
    return Boolean(this.execution?.entityType == 'ADHOC_EXECUTION');
  }

  get lastRun() {
    if (this.lastReRunResult != null) return this.lastReRunResult;
    return this;
  }

  get rerunParentExecutionResultId() {
    const lastRunId = this.lastRun?.id;
    return lastRunId ? lastRunId : this.id;
  }

  get canReRun() {
    const isExecutionRunning = this.isExecuting || this.isLatestRunExecuting;
    if (isExecutionRunning) {
      return false;
    }
    if (
      this.isFailed ||
      (this.isNotExecuted && this.testPlanResultMetric?.notExecutedCount !== this.testPlanResultMetric?.totalCount) ||
      (this.isStopped && this.testPlanResultMetric?.stoppedCount !== this.testPlanResultMetric?.totalCount  
        && this.testPlanResultMetric?.runningCount == 0)
    ) {
      return true;
    }
    return false;
  }

  isFailedTypeReRun(result) {
    return result?.reRunType != ReRunType.ALL_TESTS;
  }

  get isLatestRunExecuting() {
    return (
      this.testPlanResultMetric?.latestResult == ResultConstant.RUNNING ||
      this.testPlanResultMetric?.latestResult == ResultConstant.QUEUED
    );
  }

}
